import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Stethoscope, Plus, Pencil, Trash2 } from "lucide-react";
import { SanitarioDialog } from "@/components/SanitarioDialog";

const SAN = ["Medico", "Infermiere"];
const isSanitario = (v) => (v.qualifications || []).some((q) => SAN.includes(q));

export function SanitariList({ volunteers, setVolunteers }) {
  const [dialog, setDialog] = useState({ open: false, item: null });
  const list = volunteers.filter(isSanitario);

  const onSaved = (u) => setVolunteers((p) => (p.some((v) => v.id === u.id) ? p.map((v) => (v.id === u.id ? { ...v, ...u } : v)) : [...p, u]));
  const remove = async (v) => {
    if (!window.confirm(`Eliminare ${v.name} dall'elenco sanitari? Verrà tolto anche dai servizi futuri.`)) return;
    try {
      await api.delete(`/sanitari/${v.id}`);
      setVolunteers((p) => p.filter((x) => x.id !== v.id));
      toast.success("Sanitario eliminato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="sanitari-list">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-[#D32F2F]" /> Medici e infermieri ({list.length})
        </h3>
        <Button size="sm" data-testid="add-sanitario" onClick={() => setDialog({ open: true, item: null })} className="rounded-full h-8">
          <Plus className="h-4 w-4 mr-1" /> Aggiungi
        </Button>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessun medico o infermiere. Aggiungili con il pulsante o dando la qualifica a un volontario.</p>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {list.map((v) => (
            <div key={v.id} data-testid={`sanitario-${v.id}`} className="rounded-lg border border-border p-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-sm">{v.name}{v.external && <span className="ml-2 text-[10px] font-medium text-muted-foreground uppercase">non usa l'app</span>}</p>
                {v.email && <p className="text-xs text-muted-foreground truncate">{v.email}</p>}
                {v.phone && <a href={`tel:${v.phone}`} className="text-xs text-[#D32F2F] hover:underline">{v.phone}</a>}
                <div className="flex flex-wrap gap-1 mt-1">
                  {v.qualifications.filter((q) => SAN.includes(q)).map((q) => <Badge key={q} className="bg-[#D32F2F] text-white text-[10px]">{q}</Badge>)}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="icon" variant="ghost" title="Modifica" data-testid={`edit-sanitario-${v.id}`} onClick={() => setDialog({ open: true, item: v })} className="h-8 w-8">
                  <Pencil className="h-4 w-4" />
                </Button>
                {v.external && (
                  <Button size="icon" variant="ghost" title="Elimina" data-testid={`delete-sanitario-${v.id}`} onClick={() => remove(v)} className="h-8 w-8 text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <SanitarioDialog open={dialog.open} sanitario={dialog.item} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={onSaved} />
    </div>
  );
}
